
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export default function NewsletterVerification() {
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [autoRedirect, setAutoRedirect] = useState(false);

  useEffect(() => {
    // Check if we have ID and token in the URL
    const id = searchParams.get("id");
    const token = searchParams.get("token");

    console.log("Newsletter verification parameters:", { id, token, fullUrl: window.location.href });

    // Only auto-redirect in production or after verification completes
    const shouldAutoRedirect = window.location.hostname === "ledlr.org" || 
                              window.location.hostname === "www.ledlr.org";
    setAutoRedirect(shouldAutoRedirect);

    // If we have both, try to verify the email
    if (id && token) {
      verifyEmail(id, token);
    } else {
      setError("Lien de vérification incomplet. Veuillez vérifier l'URL ou réessayer avec le lien dans votre email.");
    }
  }, [searchParams]);

  useEffect(() => {
    // Auto-redirect after verification completes (only in production)
    let timer;
    if ((isVerified || error) && autoRedirect) {
      timer = setTimeout(() => {
        navigate('/');
      }, 5000);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isVerified, error, navigate, autoRedirect]);

  const verifyEmail = async (id: string, token: string) => {
    setIsVerifying(true);
    try {
      console.log("Calling verify-newsletter-email with URL params:", { id, token });
      
      // Fix: Properly construct the URL for the Supabase function
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "https://jqswmyqzmjdstetjqzvv.supabase.co";
      const functionUrl = `${supabaseUrl}/functions/v1/verify-newsletter-email?id=${encodeURIComponent(id)}&token=${encodeURIComponent(token)}`;
      
      const response = await fetch(functionUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // No auth token needed as this is a public function
        }
      });
      
      const data = await response.json();
      console.log("Verification response:", data);

      if (!data.success) {
        console.error("Verification failed:", data);
        setError(data.error || "Échec de la vérification");
        toast.error("Échec de la vérification: " + (data.error || "Une erreur est survenue lors de la vérification de votre email"));
      } else {
        console.log("Email verification successful:", data);
        setIsVerified(true);
        toast.success(data.message || "Votre inscription à la newsletter a été confirmée avec succès.");
      }
    } catch (err: any) {
      console.error("Exception verifying email:", err);
      setError(err.message || "Une erreur inattendue est survenue");
      toast.error(err.message || "Une erreur inattendue est survenue lors de la vérification");
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow flex items-center justify-center py-16 px-4">
        <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8 text-center">
          {isVerifying ? (
            <>
              <div className="flex justify-center mb-4">
                <Loader2 className="h-16 w-16 text-primary animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-primary mb-4">Vérification en cours...</h1>
              <p className="text-gray-600 mb-6">Veuillez patienter pendant que nous vérifions votre email.</p>
            </>
          ) : error ? (
            <>
              <div className="flex justify-center mb-4">
                <XCircle className="h-16 w-16 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-primary mb-4">Échec de la vérification</h1>
              <p className="text-gray-600 mb-6">{error}</p>
              {autoRedirect && (
                <p className="text-gray-500 text-sm mb-6">Vous allez être redirigé vers la page d'accueil dans quelques secondes.</p>
              )}
              <Button onClick={() => navigate("/")} className="bg-primary">
                Retour à l'accueil
              </Button>
            </>
          ) : (
            <>
              <div className="flex justify-center mb-4">
                <CheckCircle className="h-16 w-16 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold text-primary mb-4">Inscription confirmée !</h1>
              <p className="text-gray-600 mb-6">Votre inscription à la newsletter a été confirmée avec succès.</p>
              {autoRedirect && (
                <p className="text-gray-500 text-sm mb-6">Vous allez être redirigé vers la page d'accueil dans quelques secondes.</p>
              )}
              <Button onClick={() => navigate("/")} className="bg-primary">
                Retour à l'accueil
              </Button>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
